// ./src/contexts/TravelsContext/TravelsContext.tsx
//
// Context wrapper around the useTravels hook.
//
// Purpose:
// - Allows sibling components (list, edit drawer, selection summary, toolbar)
//   to share the same travels state without prop-drilling.
// - Each page creates its own <TravelsProvider> with its own config,
//   so TravelsPage filters don't affect DashboardPage, etc.
// - The underlying useTravels hook remains the data engine; this context
//   simply makes its return value available to the component tree.
//
// Usage:
//
//   // In a page component:
//   <TravelsProvider autoLoad={false} pageSize={20}>
//     <TravelsPageContent />
//     <TravelEditDrawer />
//   </TravelsProvider>
//
//   // In any child component:
//   const { travels, loading, create, ... } = useTravelsContext();
//
//   // In a component that may or may not be inside a provider (e.g. drawer):
//   const context = useOptionalTravelsContext();
//   const createFn = context?.create ?? props.onCreate;

import React, { createContext, useContext } from "react";
import { useTravels, UseTravelsOptions, UseTravelsReturn } from "@/hooks/useTravels";

// ============================================
// Context
// ============================================

const TravelsContext = createContext<UseTravelsReturn | null>(null);

// ============================================
// Provider
// ============================================

export interface TravelsProviderProps extends UseTravelsOptions {
  children: React.ReactNode;
}

/**
 * Wraps useTravels into a context provider.
 *
 * Each instance creates its own independent useTravels store
 * with its own filters, pagination, loading state, and error state.
 *
 * @example
 * <TravelsProvider autoLoad={false} pageSize={20} initialSort={[{ name: "firstDttm", order: "DESC" }]}>
 *   <MyPageContent />
 * </TravelsProvider>
 */
export const TravelsProvider: React.FC<TravelsProviderProps> = ({
  children,
  ...options
}) => {
  const travels = useTravels(options);

  return (
    <TravelsContext.Provider value={travels}>
      {children}
    </TravelsContext.Provider>
  );
};

// ============================================
// Hooks
// ============================================

/**
 * Access the TravelsContext. Throws if used outside a TravelsProvider.
 *
 * Use this in components that are always rendered inside a provider
 * (e.g. TravelList, TravelFiltersToolbar).
 */
export const useTravelsContext = (): UseTravelsReturn => {
  const context = useContext(TravelsContext);

  if (!context) {
    throw new Error(
      "useTravelsContext must be used within a <TravelsProvider>. " +
      "Wrap your component tree with <TravelsProvider> or use useOptionalTravelsContext() instead."
    );
  }

  return context;
};

/**
 * Access the TravelsContext if available, or return null.
 *
 * Use this in components that may or may not be inside a provider
 * (e.g. TravelEditDrawer which works both inside TravelsPage's provider
 * and standalone on DashboardPage).
 *
 * @example
 * const context = useOptionalTravelsContext();
 * const createFn = context?.create ?? props.onCreate;
 * const loadFn = context?.get ?? props.onLoad;
 */
export const useOptionalTravelsContext = (): UseTravelsReturn | null => {
  return useContext(TravelsContext);
};